export const LEAD_SOURCES = [
  'Zillow',
  'LendingTree',
  'Referral Partner',
  'Realtor',
  'Repeat Client',
  'Direct Mail',
  'Call Center',
  'Bankrate',
  'Social Media',
  'Builder Partner',
  'CPA Referral',
  'Organic Web',
].sort((a, b) => a.localeCompare(b))

export type CommissionModel = 'BPS' | 'CDS'

export const INITIAL_CDS_TIERS = [
  { id: '1', min: 1, max: 3, amount: 300 },
  { id: '2', min: 4, max: 7, amount: 500 },
  { id: '3', min: 8, max: 11, amount: 600 },
  { id: '4', min: 12, max: null, amount: 750 },
]

export interface Loan {
  id: string
  borrowerName: string
  fundedDate: string
  leadSource: string
  loanAmount: number
  commissionAmount: number
}

export interface User {
  id: string
  name: string
  title: string
  avatar: string
  model: CommissionModel
  rates?: Record<string, number> // For BPS users
  fundedMTD: number
  fundedLastMonth: number
  // Mocking the breakdown of funded loans by source for BPS calculation
  fundedBySource?: Record<string, { count: number; volume: number }>
  drawBalance: number // Carried over from previous months
  currentMonthDraw: number // Draw issued this month
  loans: Loan[] // Loan-level detail for the current month
  previousLoans: Loan[] // Loan-level detail for the previous month
  monthlyLoans: { key: string; label: string; loans: Loan[] }[] // Loan history by month, newest first
}

const firstNames = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'William',
  'Elizabeth',
  'David',
  'Barbara',
  'Richard',
  'Susan',
  'Joseph',
  'Jessica',
  'Thomas',
  'Sarah',
  'Charles',
  'Karen',
  'Christopher',
  'Lisa',
  'Daniel',
  'Nancy',
  'Matthew',
  'Betty',
  'Anthony',
  'Margaret',
  'Mark',
  'Sandra',
  'Donald',
  'Ashley',
  'Steven',
  'Kimberly',
  'Paul',
  'Emily',
  'Andrew',
  'Donna',
  'Joshua',
  'Michelle',
  'Kenneth',
  'Carol',
  'Kevin',
  'Amanda',
  'Brian',
  'Melissa',
  'George',
  'Deborah',
  'Timothy',
  'Stephanie',
  'Ronald',
  'Rebecca',
  'Edward',
  'Sharon',
  'Jason',
]
const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Perez',
  'Thompson',
  'White',
  'Harris',
  'Sanchez',
  'Clark',
  'Ramirez',
  'Lewis',
  'Robinson',
  'Walker',
  'Young',
  'Allen',
  'King',
  'Wright',
  'Scott',
  'Torres',
  'Nguyen',
  'Hill',
  'Flores',
  'Green',
  'Adams',
  'Nelson',
  'Baker',
  'Hall',
  'Rivera',
  'Campbell',
  'Mitchell',
  'Carter',
  'Roberts',
  'Gomez',
  'Phillips',
  'Evans',
  'Turner',
  'Diaz',
]

function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

// Shared loan generator for a given month. monthPrefix like '2026-05'.
function generateLoansForMonth(
  userIndex: number,
  model: CommissionModel,
  rates: Record<string, number>,
  fundedCount: number,
  monthPrefix: string,
  maxDay: number,
): Loan[] {
  const result: Loan[] = []
  if (fundedCount <= 0) return result

  if (model === 'BPS') {
    for (let j = 0; j < fundedCount; j++) {
      const source = LEAD_SOURCES[getRandomInt(0, LEAD_SOURCES.length - 1)]
      const loanAmount = getRandomInt(250000, 450000)
      result.push({
        id: `loan-${monthPrefix}-${userIndex}-${j}`,
        borrowerName: `${lastNames[getRandomInt(0, lastNames.length - 1)]}, ${firstNames[getRandomInt(0, firstNames.length - 1)]}`,
        fundedDate: `${monthPrefix}-${getRandomInt(1, maxDay).toString().padStart(2, '0')}`,
        leadSource: source,
        loanAmount,
        commissionAmount: loanAmount * ((rates[source] || 0) / 10000),
      })
    }
  } else {
    const tier = INITIAL_CDS_TIERS.find(
      (t) => fundedCount >= t.min && (t.max === null || fundedCount <= t.max),
    )
    const perLoanAmount = tier ? tier.amount : 0
    for (let j = 0; j < fundedCount; j++) {
      const source = LEAD_SOURCES[getRandomInt(0, LEAD_SOURCES.length - 1)]
      result.push({
        id: `loan-${monthPrefix}-${userIndex}-${j}`,
        borrowerName: `${lastNames[getRandomInt(0, lastNames.length - 1)]}, ${firstNames[getRandomInt(0, firstNames.length - 1)]}`,
        fundedDate: `${monthPrefix}-${getRandomInt(1, maxDay).toString().padStart(2, '0')}`,
        leadSource: source,
        loanAmount: getRandomInt(250000, 450000),
        commissionAmount: perLoanAmount,
      })
    }
  }

  result.sort(
    (a, b) =>
      new Date(b.fundedDate).getTime() - new Date(a.fundedDate).getTime(),
  )
  return result
}

export const MOCK_USERS: User[] = Array.from({ length: 55 }).map((_, i) => {
  const isCDS = Math.random() > 0.8 // ~20% CDS users
  const model: CommissionModel = isCDS ? 'CDS' : 'BPS'

  const rates: Record<string, number> = {}
  const fundedBySource: Record<string, { count: number; volume: number }> = {}
  const loans: Loan[] = []

  let totalFundedMTD = 0

  if (model === 'BPS') {
    LEAD_SOURCES.forEach((source) => {
      rates[source] = getRandomInt(45, 115)

      // Assign some random funded loans to a few sources
      if (Math.random() > 0.6) {
        const count = getRandomInt(1, 5)
        let sourceVolume = 0

        for (let j = 0; j < count; j++) {
          const loanAmount = getRandomInt(250000, 450000)
          sourceVolume += loanAmount
          const commissionAmount = loanAmount * (rates[source] / 10000)

          loans.push({
            id: `loan-${i}-${source}-${j}`,
            borrowerName: `${lastNames[getRandomInt(0, lastNames.length - 1)]}, ${firstNames[getRandomInt(0, firstNames.length - 1)]}`,
            fundedDate: `2026-06-${getRandomInt(1, 28).toString().padStart(2, '0')}`,
            leadSource: source,
            loanAmount,
            commissionAmount,
          })
        }

        fundedBySource[source] = { count, volume: sourceVolume }
        totalFundedMTD += count
      }
    })
  } else {
    totalFundedMTD = getRandomInt(2, 15)

    // For CDS, commission is flat per tier, but we'll calculate the tier first to assign the amount to each loan
    const currentTier = INITIAL_CDS_TIERS.find(
      (t) =>
        totalFundedMTD >= t.min && (t.max === null || totalFundedMTD <= t.max),
    )
    const perLoanAmount = currentTier ? currentTier.amount : 0

    for (let j = 0; j < totalFundedMTD; j++) {
      const source = LEAD_SOURCES[getRandomInt(0, LEAD_SOURCES.length - 1)]
      const loanAmount = getRandomInt(250000, 450000)

      loans.push({
        id: `loan-${i}-${j}`,
        borrowerName: `${lastNames[getRandomInt(0, lastNames.length - 1)]}, ${firstNames[getRandomInt(0, firstNames.length - 1)]}`,
        fundedDate: `2026-06-${getRandomInt(1, 28).toString().padStart(2, '0')}`,
        leadSource: source,
        loanAmount,
        commissionAmount: perLoanAmount,
      })
    }
  }

  // Sort loans by date descending
  loans.sort(
    (a, b) =>
      new Date(b.fundedDate).getTime() - new Date(a.fundedDate).getTime(),
  )

  const name = `${firstNames[i % firstNames.length]} ${lastNames[i % lastNames.length]}`

  const fundedLastMonth = Math.max(0, totalFundedMTD + getRandomInt(-3, 4))
  const previousLoans = generateLoansForMonth(
    i,
    model,
    rates,
    fundedLastMonth,
    '2026-05',
    31,
  )

  // Build a month-by-month loan history (newest first). Current and previous
  // months reuse the already-generated loan sets so totals stay consistent.
  const olderMonths = [
    { key: '2026-04', label: 'April 2026', maxDay: 30 },
    { key: '2026-03', label: 'March 2026', maxDay: 31 },
    { key: '2026-02', label: 'February 2026', maxDay: 28 },
    { key: '2026-01', label: 'January 2026', maxDay: 31 },
  ]
  const monthlyLoans = [
    { key: '2026-06', label: 'June 2026 (Current)', loans },
    { key: '2026-05', label: 'May 2026', loans: previousLoans },
    ...olderMonths.map((m) => ({
      key: m.key,
      label: m.label,
      loans: generateLoansForMonth(
        i,
        model,
        rates,
        Math.max(0, totalFundedMTD + getRandomInt(-4, 4)),
        m.key,
        m.maxDay,
      ),
    })),
  ]

  // Randomly assign some draw balances to a few users to show the feature
  const hasDrawBalance = Math.random() > 0.85
  const drawBalance = hasDrawBalance ? getRandomInt(1000, 5000) : 0
  const currentMonthDraw =
    hasDrawBalance && Math.random() > 0.5 ? getRandomInt(500, 2000) : 0

  return {
    id: `user-${i + 1}`,
    name,
    title: isCDS
      ? 'CDS Specialist'
      : Math.random() > 0.5
        ? 'Senior Loan Officer'
        : 'Loan Officer',
    avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`,
    model,
    ...(model === 'BPS' ? { rates, fundedBySource } : {}),
    fundedMTD: totalFundedMTD,
    fundedLastMonth,
    drawBalance,
    currentMonthDraw,
    loans,
    previousLoans,
    monthlyLoans,
  }
})
